import { MUIButton, MUIDialog, MUITab } from "@abb/arcadia-ui-react"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useHashTab } from "../../../../hooks/useHashTab"
import { digitalManagerApi } from "../../../../services/api"
import {
  DiMaDetailsHeader,
  DiMaDetailsHeaderProps,
} from "../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { UserContext } from "../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../models/Authorization"
import { FeedbackGet } from "../../../models/Feedback"
import { FeedbackDetails } from "./details_tab/FeedbackDetails"

const returnURL = "/feedbacks"

export function FeedbackContainer() {
  const { feedbackId } = useParams()
  const navigate = useNavigate()
  const { activeTab, handleTabChange } = useHashTab("details")
  const { user } = useContext(UserContext)

  const [feedback, setFeedback] = useState<FeedbackGet>()
  const [isCompleting, setIsCompleting] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  useEffect(() => {
    if (feedbackId) {
      digitalManagerApi
        .get<FeedbackGet>(`/feedbacks/${feedbackId}`)
        .then((res) => {
          setFeedback(res.data)
        })
        .catch(() => {
          navigate(returnURL)
        })
    }
  }, [feedbackId, navigate])

  const canComplete =
    user &&
    feedback &&
    !feedback.completionDate &&
    hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      feedback.application.id,
      DimaFeatureShortNameEnum.FB,
      AccessLevel.Write
    )

  const handleComplete = useCallback(() => {
    if (!feedbackId) {
      return
    }

    setIsCompleting(true)
    digitalManagerApi
      .patch<FeedbackGet>(`/feedbacks/${feedbackId}`, {})
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          toast.success("Feedback marked as completed")
          setFeedback(res.data)
        }
      })
      .catch(() => {
        toast.error("Cannot complete the feedback")
      })
      .finally(() => {
        setIsCompleting(false)
        setConfirmDialogOpen(false)
      })
  }, [feedbackId])

  const openConfirmDialog = useCallback(() => setConfirmDialogOpen(true), [])
  const closeConfirmDialog = useCallback(() => setConfirmDialogOpen(false), [])

  if (!feedbackId) {
    return <Navigate to={returnURL} />
  }

  if (!feedback) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: feedback.user.username,
    label: "Feedback ID",
    subtitle: feedback.id,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <DiMaDetailsHeader {...detailsHeaderProps} />
      {canComplete && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 16px" }}>
          <MUIButton
            label="Mark as Completed"
            iconName="icon_abb_dias icon-abb_completed-check"
            variant="success"
            size={40}
            isLoading={isCompleting}
            disabled={isCompleting}
            onClickButton={openConfirmDialog}
          />
        </div>
      )}
      <MUITab
        tabItems={[{ id: "details", label: "Details" }]}
        value={activeTab}
        handleChange={handleTabChange}
      />
      {activeTab === "details" && <FeedbackDetails feedback={feedback} />}

      <MUIDialog
        id="confirm-complete-dialog"
        variant="default"
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        heading="Are you sure you want to mark this feedback as completed?"
        content="This operation cannot be undone."
        secondaryButtonText="Cancel"
        secondaryButtonConfig={{ isDisabled: isCompleting }}
        primaryButtonText="Confirm"
        onClickPrimaryButton={handleComplete}
        isPrimaryButtonDisabled={isCompleting}
        primaryButtonConfig={{
          isLoading: isCompleting,
          type: "primary",
          size: 32,
          id: "confirm-complete-button",
        }}
      />
    </div>
  )
}
